import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    ChevronRight,
    Download,
    Eye,
    EyeOff,
    History,
    ListChecks,
    Megaphone,
    Package,
    Rocket,
    RotateCcw,
    Search as SearchIcon,
    SearchX,
    X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    clearLeads,
    fetchSearchLeadsById,
    fetchSearchHistory,
    markSearchEventFailed,
    performAiSearch,
    pollSearchResults,
    setActiveSearch,
    updateSearchFromEventSnapshot,
} from "../store/leadsSlice";
import { fetchCurrentUsage } from "../store/authSlice";
import { fetchConnections } from "../store/connectionsSlice";
import { fetchOrganizationContext } from "../store/organizationContextSlice";
import { fetchUnreadNotificationCount } from "../store/notificationsSlice";
import { exportLeadsToCsv } from "../utils/exportCsv";
import { formatSearchHistoryDate } from "../utils/formatDate";
import useSearchEvents from "../hooks/useSearchEvents";
import useElementInView from "../hooks/useElementInView";
import useScrollSelectedIntoView from "../hooks/useScrollSelectedIntoView";
import { ROUTES } from "../config/constants";
import CampaignBlueprintModal from "../components/CampaignBlueprintModal";
import DashboardRecentSection from "../components/dashboard/DashboardRecentSection";
import FloatingActionsMenu from "../components/common/FloatingActionsMenu";
import PageContainer from "../components/layout/PageContainer";
import OrganizationContextForm from "../components/organization/OrganizationContextForm";
import StatsCard from "../components/Search/StatsCard";
import SearchHistoryPanel from "../components/Search/SearchHistoryPanel";
import SearchListFilters from "../components/Search/SearchListFilters";
import {
    getSearchStatusBadge,
    isSearchCompleted,
    isSearchFailed,
    isSearchProcessing,
    normalizeSearchStatus,
} from "../utils/searchStatusUtils";
import {
    formatCreditValue,
    getCreditUsage,
    hasAvailableCredits,
} from "../utils/subscription";
import { hasActiveSenderConnection } from "../utils/connectionGuard";

const SEARCH_POLL_INTERVAL = 1500;
const LEGACY_POLL_FALLBACK_MAX_ATTEMPTS = 20;
const EXPORT_CSV_PLAN_MESSAGE =
    "CSV export is not available on your current plan.";

const getUniqueLeadIds = (leads) => [
    ...new Set(
        (Array.isArray(leads) ? leads : [])
            .map((lead) => lead?.id)
            .filter(Boolean),
    ),
];

const wait = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const getDateTime = (value) => {
    if (!value) return 0;

    const timestamp = new Date(value).getTime();

    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const REQUIRED_ORGANIZATION_CONTEXT_FIELDS = [
    "company_description",
    "brand_voice",
    "target_icp",
    "core_value_proposition",
    "website_url",
];

const hasValidOrganizationContext = (context) =>
    REQUIRED_ORGANIZATION_CONTEXT_FIELDS.every((fieldName) =>
        String(context?.[fieldName] || "").trim(),
    );

const validateSearchTerm = (value) => {
    if (!value.trim()) {
        return "Search term is required.";
    }

    if (/['&\\]/.test(value)) {
        return "Search cannot contain ', &, or \\ characters. Please remove them and try again.";
    }

    return "";
};

const getSearchRecordId = (search) => search?.search_id || search?.id || "";

const getSearchResultCount = (search) => {
    const resultCollections = [search?.results, search?.leads];

    for (const results of resultCollections) {
        if (Array.isArray(results)) {
            return results.length;
        }
    }

    const countFields = [
        search?.lead_count,
        search?.result_count,
        search?.results_count,
        search?.total_leads,
        search?.total_results,
        search?.leads_count,
    ];

    for (const value of countFields) {
        if (value === "" || value === null || value === undefined) {
            continue;
        }

        const count = Number(value);

        if (Number.isFinite(count) && count >= 0) {
            return count;
        }
    }

    return null;
};

const getSearchEventId = (eventData, fallbackSearchId = "") =>
    eventData?.search_id || fallbackSearchId || eventData?.id || "";

const SearchLeads = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const topActionsRef = useRef(null);
    const areTopActionsVisible = useElementInView(topActionsRef, {
        rootMargin: "-64px 0px 0px 0px",
        threshold: 0.9,
        minimumVisibleRatio: 0.9,
        topOffset: 64,
    });

    const {
        leadResults,
        status,
        searchStatus,
        error,
        activeSearchId,
        searchHistory,
        searchHistoryStatus,
        searchHistoryError,
    } = useSelector((state) => state.leads);

    const { credits, csvExportEnabled } = useSelector((state) => state.auth);
    const {
        data: organizationContext,
        status: organizationContextStatus,
    } = useSelector((state) => state.organizationContext);
    const [
        hasSavedInlineOrganizationContext,
        setHasSavedInlineOrganizationContext,
    ] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [submittedTerm, setSubmittedTerm] = useState("");
    const [limitError, setLimitError] = useState("");
    const [validationError, setValidationError] = useState("");
    const [campaignPreparationError, setCampaignPreparationError] =
        useState("");
    const [preparingCampaignQuery, setPreparingCampaignQuery] = useState("");
    const [campaignLeadIds, setCampaignLeadIds] = useState([]);
    const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
    const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
    const [
        shouldStartBlueprintAtDesignStep,
        setShouldStartBlueprintAtDesignStep,
    ] = useState(false);
    const [showDetail, setShowDetail] = useState(null);
    const [activeSearchEvents, setActiveSearchEvents] = useState(null);
    const [searchNotFoundSearchId, setSearchNotFoundSearchId] = useState("");
    const [dashboardSearchScrollTargetId, setDashboardSearchScrollTargetId] =
        useState("");
    const [searchListFilterTerm, setSearchListFilterTerm] = useState("");
    const [searchListStatusFilter, setSearchListStatusFilter] =
        useState("all");
    const searchFallbackRequest = useRef(0);
    const campaignPreparationRequest = useRef(0);
    const consumedNavigationCampaignRequest = useRef("");
    const consumedNavigationSearchRequest = useRef("");
    const failedSearchToastIds = useRef(new Set());
    const completedSearchFetchIds = useRef(new Set());
    const terminalNotificationRefreshIds = useRef(new Set());

    const currentTrimmedValue = inputValue.trim();
    const searchLeadUsage = getCreditUsage({
        used: credits?.search_leads_used,
        limit: credits?.search_lead_limit,
        remaining: credits?.search_leads_remaining,
    });

    const isSubmittedTermActive =
        Boolean(submittedTerm) && currentTrimmedValue === submittedTerm;
    const shouldShowProcessing =
        searchStatus === "processing" &&
        isSubmittedTermActive &&
        searchNotFoundSearchId !== activeSearchId;
    const isSearchNotFoundForCurrentSearch =
        Boolean(searchNotFoundSearchId) &&
        searchNotFoundSearchId === activeSearchId;
    const shouldShowResults =
        searchStatus === "completed" && isSubmittedTermActive;
    const shouldShowLeadLoading = status === "loading" && isSubmittedTermActive;
    const totalLeadCount = leadResults.length;
    const hasActiveSearchWithVisibleResults =
        shouldShowResults && leadResults.length > 0 && Boolean(activeSearchId);
    const canShowCampaignActions = hasActiveSearchWithVisibleResults;
    const canExportCsv = csvExportEnabled === true;
    const canExportCurrentResults =
        canExportCsv && shouldShowResults && leadResults.length > 0;
    const exportCsvTitle = canExportCsv
        ? "Export current lead results as CSV."
        : EXPORT_CSV_PLAN_MESSAGE;
    const isPreparingCampaign = Boolean(preparingCampaignQuery);
    const isOrganizationContextChecking =
        organizationContextStatus === "idle" ||
        organizationContextStatus === "loading";
    const isOrganizationContextMissing =
        !isOrganizationContextChecking &&
        !hasSavedInlineOrganizationContext &&
        !hasValidOrganizationContext(organizationContext);
    const isSearchWorkspaceLocked =
        isOrganizationContextChecking || isOrganizationContextMissing;
    const searchStats = useMemo(() => {
        const safeSearchHistory = Array.isArray(searchHistory)
            ? searchHistory
            : [];

        return safeSearchHistory.reduce(
            (stats, search) => {
                const normalizedStatus = normalizeSearchStatus(search?.status);
                const resultCount = getSearchResultCount(search);

                if (normalizedStatus === "completed") {
                    stats.completed += 1;
                    if (resultCount === 0) {
                        stats.noResults += 1;
                    }
                } else if (normalizedStatus === "processing") {
                    stats.processing += 1;
                } else if (
                    normalizedStatus === "failed" ||
                    normalizedStatus === "no_results"
                ) {
                    stats.noResults += 1;
                }

                return stats;
            },
            {
                total: safeSearchHistory.length,
                completed: 0,
                processing: 0,
                noResults: 0,
            },
        );
    }, [searchHistory]);
    const recentSearches = useMemo(() => {
        if (!Array.isArray(searchHistory)) return [];

        return [...searchHistory].sort(
            (first, second) =>
                getDateTime(second?.created_at) -
                getDateTime(first?.created_at),
        );
    }, [searchHistory]);
    const filteredRecentSearches = useMemo(() => {
        const normalizedSearchTerm = searchListFilterTerm
            .trim()
            .toLowerCase();
        const shouldApplyTextFilter = normalizedSearchTerm.length >= 3;

        return recentSearches.filter((search) => {
            const matchesText =
                !shouldApplyTextFilter ||
                String(search?.query_string || "")
                    .toLowerCase()
                    .includes(normalizedSearchTerm);
            const matchesStatus =
                searchListStatusFilter === "all" ||
                normalizeSearchStatus(search?.status) ===
                    searchListStatusFilter;

            return matchesText && matchesStatus;
        });
    }, [
        recentSearches,
        searchListFilterTerm,
        searchListStatusFilter,
    ]);
    const hasSearchListFilters =
        searchListFilterTerm.trim().length >= 3 ||
        searchListStatusFilter !== "all";
    const shouldShowRecentSearches =
        searchHistoryStatus === "idle" ||
        searchHistoryStatus === "loading" ||
        searchHistoryStatus === "failed" ||
        recentSearches.length > 0;
    const shouldScrollDashboardSearchIntoView =
        Boolean(dashboardSearchScrollTargetId) &&
        filteredRecentSearches.some(
            (search) =>
                getSearchRecordId(search) === dashboardSearchScrollTargetId,
        );
    const handleDashboardSearchScrolled = useCallback(() => {
        setDashboardSearchScrollTargetId("");
    }, []);
    const selectedRecentSearchRef = useScrollSelectedIntoView({
        selectedId: dashboardSearchScrollTargetId,
        enabled: shouldScrollDashboardSearchIntoView,
        onScrolled: handleDashboardSearchScrolled,
    });

    const getCampaignsRoute = (searchId) =>
        searchId
            ? `${ROUTES.CAMPAIGNS}?searchId=${encodeURIComponent(searchId)}`
            : ROUTES.CAMPAIGNS;

    useEffect(() => {
        dispatch(fetchCurrentUsage());
        dispatch(fetchOrganizationContext());
        dispatch(fetchSearchHistory());
    }, [dispatch]);

    useEffect(() => {
        if (status === "succeeded" && searchStatus === "completed") {
            dispatch(fetchCurrentUsage());
            dispatch(fetchSearchHistory());
        }
    }, [dispatch, searchStatus, status]);

    const runSearch = useCallback(
        async (term) => {
            const trimmed = term.trim();

            setLimitError("");
            setValidationError("");
            setCampaignPreparationError("");
            const fallbackRequestId = searchFallbackRequest.current + 1;
            searchFallbackRequest.current = fallbackRequestId;

            if (!trimmed) {
                setSubmittedTerm("");
                setValidationError("Search term is required.");
                dispatch(clearLeads());
                return;
            }

            const validationError = validateSearchTerm(trimmed);

            if (validationError) {
                setValidationError(validationError);
                dispatch(clearLeads());
                return;
            }

            if (!hasAvailableCredits(searchLeadUsage)) {
                setSubmittedTerm("");
                setLimitError(
                    Number.isFinite(searchLeadUsage.limit)
                        ? `You have reached your monthly lead limit of ${formatCreditValue(searchLeadUsage.limit)}.`
                        : "You have reached your monthly lead search limit.",
                );
                dispatch(clearLeads());
                return;
            }

            setInputValue(trimmed);
            setSubmittedTerm(trimmed);
            setActiveSearchEvents(null);
            setSearchNotFoundSearchId("");

            const searchAction = await dispatch(performAiSearch(trimmed));

            if (performAiSearch.rejected.match(searchAction)) {
                setDashboardSearchScrollTargetId("");
                return;
            }

            const searchResult = searchAction.payload;
            const isSearchInProgress = isSearchProcessing(
                searchResult?.searchStatus,
            );

            if (
                isSearchInProgress &&
                searchResult?.searchId &&
                searchResult?.eventsUrl
            ) {
                setActiveSearchEvents({
                    searchId: searchResult.searchId,
                    eventsUrl: searchResult.eventsUrl,
                });
            } else if (isSearchInProgress && !searchResult?.eventsUrl) {
                console.warn(
                    "events_url missing; falling back to legacy poll_url flow.",
                );

                if (!searchResult?.pollUrl) {
                    return;
                }

                // poll_url is kept only as a bounded fallback for legacy responses without events_url.
                let fallbackPollUrl = searchResult.pollUrl;

                for (
                    let attempt = 0;
                    attempt < LEGACY_POLL_FALLBACK_MAX_ATTEMPTS;
                    attempt += 1
                ) {
                    await wait(SEARCH_POLL_INTERVAL);

                    if (searchFallbackRequest.current !== fallbackRequestId) {
                        return;
                    }

                    const pollAction = await dispatch(
                        pollSearchResults(fallbackPollUrl),
                    );

                    if (pollSearchResults.rejected.match(pollAction)) {
                        return;
                    }

                    const pollResult = pollAction.payload;

                    if (!isSearchProcessing(pollResult?.searchStatus)) {
                        return;
                    }

                    fallbackPollUrl = pollResult?.pollUrl || fallbackPollUrl;
                }

                if (searchFallbackRequest.current === fallbackRequestId) {
                    dispatch(fetchSearchHistory());
                }
            }
        },
        [dispatch, searchLeadUsage],
    );

    const fetchCompletedSearchResults = useCallback(
        async (searchId) => {
            if (!searchId) return false;

            if (completedSearchFetchIds.current.has(searchId)) {
                return true;
            }

            completedSearchFetchIds.current.add(searchId);

            try {
                await dispatch(fetchSearchLeadsById(searchId)).unwrap();
                return true;
            } catch (fetchError) {
                completedSearchFetchIds.current.delete(searchId);
                throw fetchError;
            }
        },
        [dispatch],
    );

    const refreshUnreadCountAfterTerminalSearchEvent = useCallback(
        (searchId) => {
            const refreshKey = searchId || "unknown-terminal-search-event";

            if (terminalNotificationRefreshIds.current.has(refreshKey)) {
                return;
            }

            terminalNotificationRefreshIds.current.add(refreshKey);
            dispatch(fetchUnreadNotificationCount());
        },
        [dispatch],
    );

    const handleSearchEventsCompleted = useCallback(
        async (eventData) => {
            const completedSearchId = getSearchEventId(
                eventData,
                activeSearchEvents?.searchId,
            );

            refreshUnreadCountAfterTerminalSearchEvent(completedSearchId);

            if (!completedSearchId) {
                dispatch(
                    markSearchEventFailed(
                        "Completed search is missing its search ID.",
                    ),
                );
                setActiveSearchEvents(null);
                return;
            }

            try {
                await fetchCompletedSearchResults(completedSearchId);
            } catch {
                dispatch(
                    markSearchEventFailed(
                        "Completed search results could not be loaded.",
                    ),
                );
                toast.error(
                    "Completed search results could not be loaded. Please try again.",
                );
            } finally {
                setActiveSearchEvents(null);
            }
        },
        [
            activeSearchEvents?.searchId,
            dispatch,
            fetchCompletedSearchResults,
            refreshUnreadCountAfterTerminalSearchEvent,
        ],
    );

    const showSearchFailedToastOnce = useCallback((searchId) => {
        const toastKey = searchId || "active-search";

        if (failedSearchToastIds.current.has(toastKey)) {
            return;
        }

        failedSearchToastIds.current.add(toastKey);
        toast.error("Search failed. Please try again.");
    }, []);

    const handleSearchEventsFailed = useCallback(
        (eventData) => {
            const failedSearchId = getSearchEventId(
                eventData,
                activeSearchEvents?.searchId,
            );

            refreshUnreadCountAfterTerminalSearchEvent(failedSearchId);
            dispatch(markSearchEventFailed("Search failed. Please try again."));
            dispatch(fetchSearchHistory());
            showSearchFailedToastOnce(failedSearchId);
            setActiveSearchEvents(null);
        },
        [
            activeSearchEvents?.searchId,
            dispatch,
            refreshUnreadCountAfterTerminalSearchEvent,
            showSearchFailedToastOnce,
        ],
    );

    const handleSearchEventsSnapshot = useCallback(
        (eventData) => {
            const snapshotSearchId = getSearchEventId(
                eventData,
                activeSearchEvents?.searchId,
            );

            dispatch(updateSearchFromEventSnapshot(eventData));

            // Snapshot data is the latest backend state, even if submit initially returned processing.
            if (isSearchCompleted(eventData?.status)) {
                handleSearchEventsCompleted(eventData);
            } else if (isSearchFailed(eventData?.status)) {
                refreshUnreadCountAfterTerminalSearchEvent(snapshotSearchId);
                dispatch(
                    markSearchEventFailed("Search failed. Please try again."),
                );
                dispatch(fetchSearchHistory());
                showSearchFailedToastOnce(snapshotSearchId);
                setActiveSearchEvents(null);
            }
        },
        [
            activeSearchEvents?.searchId,
            dispatch,
            handleSearchEventsCompleted,
            refreshUnreadCountAfterTerminalSearchEvent,
            showSearchFailedToastOnce,
        ],
    );

    const {
        error: searchEventsError,
        errorSearchId: searchEventsErrorSearchId,
    } = useSearchEvents({
        searchId: activeSearchEvents?.searchId,
        eventsUrl: activeSearchEvents?.eventsUrl,
        enabled: Boolean(
            activeSearchEvents?.searchId && activeSearchEvents?.eventsUrl,
        ),
        onSnapshot: handleSearchEventsSnapshot,
        onCompleted: handleSearchEventsCompleted,
        onFailed: handleSearchEventsFailed,
    });

    useEffect(() => {
        if (
            !activeSearchEvents?.searchId ||
            !searchEventsError ||
            searchEventsErrorSearchId !== activeSearchEvents.searchId
        ) {
            return;
        }

        if (
            String(searchEventsError).trim().toLowerCase() ===
            "search not found"
        ) {
            setSearchNotFoundSearchId(activeSearchEvents.searchId);
            setActiveSearchEvents(null);
            return;
        }

        dispatch(markSearchEventFailed(searchEventsError));
        dispatch(fetchSearchHistory());
        toast.error(
            "Unable to connect to live search updates. Please try again.",
        );
        setActiveSearchEvents(null);
    }, [
        activeSearchEvents?.searchId,
        dispatch,
        searchEventsError,
        searchEventsErrorSearchId,
    ]);

    useEffect(() => {
        setShowDetail(null);
    }, [leadResults]);

    const handleSearch = (event) => {
        event?.preventDefault();

        if (
            isPreparingCampaign ||
            isSearchWorkspaceLocked
        ) {
            return;
        }

        setDashboardSearchScrollTargetId("");
        runSearch(inputValue);
    };

    const handleInlineOrganizationContextSaved = useCallback(() => {
        setHasSavedInlineOrganizationContext(true);
        setLimitError("");
        setValidationError("");
        setCampaignPreparationError("");
    }, []);

    const handleOpenSearchHistory = () => {
        setIsHistoryPanelOpen(true);
        dispatch(fetchSearchHistory());
    };

    const handleSelectHistorySearch = (search) => {
        if (isOrganizationContextChecking || isOrganizationContextMissing) {
            return;
        }

        const queryString = search?.query_string?.trim();

        if (!queryString) {
            return;
        }

        setInputValue(queryString);
        setIsHistoryPanelOpen(false);
        setDashboardSearchScrollTargetId("");
        runSearch(queryString);
    };

    useEffect(() => {
        const dashboardSearch = location.state?.activateSearch || null;

        if (!dashboardSearch) return;

        if (isOrganizationContextChecking || isOrganizationContextMissing) {
            return;
        }

        const searchId = dashboardSearch.id || null;
        const queryString = dashboardSearch.queryString?.trim();
        const requestKey = `${searchId || ""}:${queryString || ""}`;

        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: null,
        });

        if (!queryString) {
            setValidationError("Search term is required.");
            return;
        }

        if (consumedNavigationSearchRequest.current === requestKey) {
            return;
        }

        consumedNavigationSearchRequest.current = requestKey;
        setDashboardSearchScrollTargetId(searchId || "");
        runSearch(queryString);
    }, [
        location.pathname,
        location.search,
        location.state,
        navigate,
        runSearch,
        isOrganizationContextChecking,
        isOrganizationContextMissing,
    ]);

    const verifyActiveSenderConnection = useCallback(async () => {
        try {
            const latestConnections = await dispatch(
                fetchConnections(),
            ).unwrap();

            if (hasActiveSenderConnection(latestConnections)) {
                return true;
            }

            toast.error(
                "Please connect an active email sender before creating a campaign.",
            );
            navigate(ROUTES.CONNECTIONS);
            return false;
        } catch {
            toast.error(
                "Unable to verify sender connection. Please try again.",
            );
            return false;
        }
    }, [dispatch, navigate]);

    const handleCreateCampaign = async () => {
        if (isPreparingCampaign) return;

        const leadIds = getUniqueLeadIds(leadResults);

        setCampaignPreparationError("");
        setShouldStartBlueprintAtDesignStep(false);

        if (leadIds.length === 0) {
            setCampaignPreparationError(
                "No leads are available for this campaign.",
            );
            return;
        }

        const canStartCampaign = await verifyActiveSenderConnection();

        if (!canStartCampaign) return;

        setCampaignLeadIds(leadIds);
        setIsCampaignModalOpen(true);
    };

    const handleViewCampaigns = () => {
        if (!activeSearchId) return;

        navigate(getCampaignsRoute(activeSearchId));
    };

    const handleViewAllCampaigns = () => {
        setIsHistoryPanelOpen(false);
        navigate(ROUTES.CAMPAIGNS);
    };

    const handleCreateCampaignFromHistory = useCallback(async (search, options = {}) => {
        const {
            closeHistoryPanel = true,
            openCampaignModal = true,
        } = options;

        if (isPreparingCampaign) {
            return {
                ok: false,
                message: "Campaign preparation is already running.",
            };
        }

        if (isOrganizationContextChecking || isOrganizationContextMissing) {
            return {
                ok: false,
                message:
                    "Organization context is required before creating a campaign.",
                showPanelError: false,
            };
        }

        const searchId = getSearchRecordId(search);
        const queryString = search?.query_string?.trim();

        setCampaignPreparationError("");
        setShouldStartBlueprintAtDesignStep(false);

        if (!searchId) {
            const message = "This search is missing its search record ID.";

            if (closeHistoryPanel) {
                setIsHistoryPanelOpen(false);
            }
            setCampaignPreparationError(message);

            return { ok: false, message };
        }

        if (!queryString) {
            const message = "This search is missing its search query.";

            if (closeHistoryPanel) {
                setIsHistoryPanelOpen(false);
            }
            setCampaignPreparationError(message);

            return { ok: false, message };
        }

        const canStartCampaign = await verifyActiveSenderConnection();

        if (!canStartCampaign) {
            return {
                ok: false,
                message:
                    "Please connect an active email sender before creating a campaign.",
                showPanelError: false,
            };
        }

        const requestId = campaignPreparationRequest.current + 1;
        campaignPreparationRequest.current = requestId;

        dispatch(
            setActiveSearch({
                id: searchId,
                query: queryString,
            }),
        );
        setInputValue(queryString);
        setSubmittedTerm(queryString);
        if (closeHistoryPanel) {
            setIsHistoryPanelOpen(false);
        }
        setCampaignLeadIds([]);
        setPreparingCampaignQuery(queryString);
        dispatch(clearLeads());

        try {
            const searchAction = await dispatch(
                performAiSearch(queryString),
            );

            if (performAiSearch.rejected.match(searchAction)) {
                throw new Error("Lead search failed.");
            }

            let searchResult = searchAction.payload;
            let currentPollUrl = searchResult?.pollUrl;

            while (searchResult?.searchStatus === "processing") {
                if (
                    campaignPreparationRequest.current !== requestId ||
                    !currentPollUrl
                ) {
                    throw new Error("Lead search polling was interrupted.");
                }

                await wait(SEARCH_POLL_INTERVAL);

                if (campaignPreparationRequest.current !== requestId) {
                    return {
                        ok: false,
                        message: "Campaign preparation was cancelled.",
                    };
                }

                const pollAction = await dispatch(
                    pollSearchResults(currentPollUrl),
                );

                if (pollSearchResults.rejected.match(pollAction)) {
                    throw new Error("Lead search polling failed.");
                }

                searchResult = pollAction.payload;
                currentPollUrl = searchResult?.pollUrl || currentPollUrl;
            }

            if (campaignPreparationRequest.current !== requestId) {
                return {
                    ok: false,
                    message: "Campaign preparation was cancelled.",
                };
            }

            const leadIds = getUniqueLeadIds(searchResult?.leadResults);

            if (leadIds.length === 0) {
                const message =
                    "No leads are available for this search, so a campaign cannot be created.";

                setCampaignPreparationError(message);

                return { ok: false, message };
            }

            setCampaignLeadIds(leadIds);
            if (openCampaignModal) {
                setIsCampaignModalOpen(true);
            }

            return { ok: true, leadIds };
        } catch {
            if (campaignPreparationRequest.current === requestId) {
                const message =
                    "Unable to load leads for this search. Please try again.";

                setCampaignPreparationError(message);

                return { ok: false, message };
            }

            return {
                ok: false,
                message: "Campaign preparation was cancelled.",
            };
        } finally {
            if (campaignPreparationRequest.current === requestId) {
                setPreparingCampaignQuery("");
            }
        }
    }, [
        dispatch,
        isOrganizationContextChecking,
        isOrganizationContextMissing,
        isPreparingCampaign,
        verifyActiveSenderConnection,
    ]);

    useEffect(() => {
        const historySearch =
            location.state?.startCampaignForHistorySearch || null;
        const startAtDesignStep =
            location.state?.startCampaignAtDesignStep === true;

        if (!historySearch) return;

        if (isOrganizationContextChecking || isOrganizationContextMissing) {
            return;
        }

        const searchId =
            historySearch.search_id || historySearch.id || "";
        const queryString = historySearch.queryString?.trim();
        const requestKey = `${searchId || ""}:${queryString || ""}:${
            startAtDesignStep ? "design" : "product"
        }`;

        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: null,
        });

        if (!searchId || !queryString) {
            setCampaignPreparationError(
                "This search cannot be used to create a campaign.",
            );
            return;
        }

        if (consumedNavigationCampaignRequest.current === requestKey) {
            return;
        }

        consumedNavigationCampaignRequest.current = requestKey;
        const prepareCampaign = async () => {
            const result = await handleCreateCampaignFromHistory(
                {
                    id: searchId,
                    query_string: queryString,
                },
                {
                    openCampaignModal: !startAtDesignStep,
                },
            );

            if (startAtDesignStep && result?.ok) {
                setShouldStartBlueprintAtDesignStep(true);
                setIsCampaignModalOpen(true);
            }
        };

        prepareCampaign();
    }, [
        handleCreateCampaignFromHistory,
        location.pathname,
        location.search,
        location.state,
        navigate,
        isOrganizationContextChecking,
        isOrganizationContextMissing,
    ]);

    const handleCloseCampaignModal = () => {
        setIsCampaignModalOpen(false);
        setCampaignLeadIds([]);
        setShouldStartBlueprintAtDesignStep(false);
    };

    const handleViewCampaignsFromHistory = (searchId) => {
        if (!searchId) return;

        setIsHistoryPanelOpen(false);
        navigate(getCampaignsRoute(searchId));
    };

    const handleViewDetail = (id) => {
        setShowDetail((prev) => (prev === id ? null : id));
    };

    const preventDetailClipboard = (event) => {
        event.preventDefault();
    };

    const handleExportCsv = () => {
        if (!canExportCsv) {
            toast.warning(EXPORT_CSV_PLAN_MESSAGE);
            return;
        }

        if (!shouldShowResults || !leadResults.length) return;

        exportLeadsToCsv(leadResults, `leads-${Date.now()}.csv`);
    };

    const handleClear = () => {
        campaignPreparationRequest.current += 1;
        setInputValue("");
        setSubmittedTerm("");
        setLimitError("");
        setValidationError("");
        setCampaignPreparationError("");
        setPreparingCampaignQuery("");
        setCampaignLeadIds([]);
        setShowDetail(null);
        setActiveSearchEvents(null);
        setSearchNotFoundSearchId("");
        setDashboardSearchScrollTargetId("");
        searchFallbackRequest.current += 1;
        dispatch(clearLeads());
    };

    const pageActions = [
        {
            label: "My Products",
            icon: Package,
            to: ROUTES.PRODUCTS,
        },
        {
            label: "My Campaigns",
            icon: Megaphone,
            onClick: handleViewAllCampaigns,
        },
        {
            label: "My Searches",
            icon: History,
            onClick: handleOpenSearchHistory,
            disabled: isSearchWorkspaceLocked,
        },
    ];
    const searchWorkspacePanelHeightStyle = {
        "--search-workspace-panel-height":
            "clamp(34rem, calc(100vh - 18rem), 46rem)",
    };

    return (
        <div className="min-h-screen bg-[#F8F8FE]">
            <PageContainer className="py-4 pb-20 sm:pb-20">
                <header className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                            Search Leads
                        </h1>
                        <p className="mt-2 max-w-2xl text-sm text-slate-600">
                            Search with AI, save useful queries, and keep your
                            last search term ready.
                        </p>
                    </div>

                    <div
                        ref={topActionsRef}
                        className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-start lg:ml-auto lg:justify-end"
                    >
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.PRODUCTS)}
                            className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:flex-none sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Package size={16} aria-hidden="true" />
                            <span className="sm:hidden">Products</span>
                            <span className="hidden sm:inline">
                                My Products
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={handleViewAllCampaigns}
                            className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl bg-indigo-50 border border-indigo-100 px-3 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:flex-none sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Megaphone size={16} aria-hidden="true" />
                            <span className="sm:hidden">Campaigns</span>
                            <span className="hidden sm:inline">
                                My Campaigns
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={handleOpenSearchHistory}
                            disabled={isSearchWorkspaceLocked}
                            className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <History size={16} aria-hidden="true" />
                            <span>My Searches</span>
                        </button>
                    </div>
                </header>

                {!isSearchWorkspaceLocked && (
                    <section
                        aria-label="Search summary"
                        className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4"
                    >
                        <StatsCard
                            title="Total Searches"
                            value={searchStats.total}
                            icon={History}
                            accent="purple"
                        />
                        <StatsCard
                            title="Completed Searches"
                            value={searchStats.completed}
                            icon={ListChecks}
                            accent="green"
                        />
                        <StatsCard
                            title="Processing Searches"
                            value={searchStats.processing}
                            icon={RotateCcw}
                            accent="amber"
                        />
                        <StatsCard
                            title="No Result Searches"
                            value={searchStats.noResults}
                            icon={X}
                            accent="red"
                        />
                    </section>
                )}

                {isOrganizationContextChecking ? (
                    <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4 text-sm font-semibold text-indigo-800 shadow-sm">
                        Checking organization context...
                    </div>
                ) : isOrganizationContextMissing ? (
                    <section className="rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm sm:p-6 lg:p-8">
                        <p className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800">
                            Before performing search, please create organization context.
                        </p>
                        <OrganizationContextForm
                            initialData={organizationContext}
                            onSaved={handleInlineOrganizationContextSaved}
                        />
                    </section>
                ) : (
                    <div
                        style={searchWorkspacePanelHeightStyle}
                        className="flex min-w-0 flex-col gap-4 sm:gap-5 lg:grid lg:grid-cols-[440px_minmax(0,1fr)] xl:grid-cols-[460px_minmax(0,1fr)] lg:items-stretch"
                    >
                        {shouldShowRecentSearches && (
                            <div className="order-3 min-w-0 lg:h-[var(--search-workspace-panel-height)]">
                                <DashboardRecentSection
                                    title="Searches"
                                    icon={SearchIcon}
                                    items={filteredRecentSearches}
                                    status={searchHistoryStatus}
                                    error={searchHistoryError}
                                    errorMessage="Unable to load recent searches."
                                    emptyTitle={
                                        hasSearchListFilters
                                            ? "No searches match your filters."
                                            : "No recent searches yet."
                                    }
                                    emptyDescription={
                                        hasSearchListFilters
                                            ? "Try another search term or status."
                                            : "Run a lead search to see it here."
                                    }
                                    scrollAllItems
                                    heightClassName="h-[330px] lg:h-full"
                                    getItemKey={(search, index) =>
                                        search?.id || `recent-search-${index}`
                                    }
                                    renderItem={(search) => {
                                        const effectiveSearch = search;
                                        const badge = getSearchStatusBadge(
                                            effectiveSearch?.status,
                                        );
                                        const effectiveSearchId =
                                            getSearchRecordId(effectiveSearch);
                                        const isActiveRecentSearch =
                                            hasActiveSearchWithVisibleResults &&
                                            Boolean(effectiveSearchId) &&
                                            effectiveSearchId ===
                                                activeSearchId;

                                        return (
                                            <div
                                                ref={
                                                    dashboardSearchScrollTargetId ===
                                                        effectiveSearchId
                                                        ? selectedRecentSearchRef
                                                        : null
                                                }
                                                className={`group flex w-full min-w-0 items-start gap-3 border-b border-l-4 border-b-slate-100 px-3.5 py-3 text-left transition-colors ${
                                                    isActiveRecentSearch
                                                        ? "border-l-[#f97316] bg-[#fff7ed] shadow-[inset_0_0_0_1px_rgba(249,115,22,0.16)]"
                                                        : "border-l-transparent odd:bg-slate-50 even:bg-white hover:bg-amber-50/50"
                                                }`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleSelectHistorySearch(
                                                            effectiveSearch,
                                                        )
                                                    }
                                                    aria-label={`Run search ${effectiveSearch?.query_string || "history"}`}
                                                    className="flex min-w-0 flex-1 items-start justify-between gap-3 text-left focus:outline-none focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-200"
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate text-sm font-semibold text-slate-900">
                                                            {effectiveSearch?.query_string ||
                                                                "Untitled search"}
                                                        </p>
                                                        <p className="mt-1 text-xs text-slate-500">
                                                            {formatSearchHistoryDate(
                                                                effectiveSearch?.created_at,
                                                            )}
                                                        </p>
                                                    </div>

                                                    {badge.label ? (
                                                        <span
                                                            className={`inline-flex w-fit shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${badge.className}`}
                                                        >
                                                            {badge.label}
                                                        </span>
                                                    ) : null}
                                                </button>

                                                <ChevronRight
                                                    size={17}
                                                    className="mt-1 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-indigo-600"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        )}

                        <div className="order-1 min-w-0 lg:col-span-2">
                            <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-[minmax(280px,0.85fr)_minmax(0,2.35fr)] lg:grid-cols-[440px_minmax(0,1fr)] xl:grid-cols-[460px_minmax(0,1fr)] md:items-center">
                                {(recentSearches.length > 0 ||
                                    hasSearchListFilters) && (
                                    <div className="min-w-0">
                                        <SearchListFilters
                                            searchValue={searchListFilterTerm}
                                            statusValue={searchListStatusFilter}
                                            onSearchChange={
                                                setSearchListFilterTerm
                                            }
                                            onStatusChange={
                                                setSearchListStatusFilter
                                            }
                                        />
                                    </div>
                                )}

                                <form
                                    onSubmit={handleSearch}
                                    className={`flex min-w-0 items-center gap-2 ${
                                        recentSearches.length > 0 ||
                                        hasSearchListFilters
                                            ? "md:col-start-2"
                                            : "md:col-span-2"
                                    }`}
                                >
                                    <input
                                        type="text"
                                        placeholder="Search by company, contact, industry..."
                                        autoFocus
                                        value={inputValue}
                                        disabled={isPreparingCampaign}
                                        onChange={(e) => {
                                            setInputValue(e.target.value);
                                            setValidationError("");
                                        }}
                                        className="h-10 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100 sm:px-4"
                                    />
                                    <button
                                        type="submit"
                                        aria-label={
                                            status === "loading"
                                                ? "Searching for leads"
                                                : "Search leads"
                                        }
                                        disabled={
                                            !currentTrimmedValue ||
                                            status === "loading" ||
                                            isPreparingCampaign ||
                                            isSearchWorkspaceLocked
                                        }
                                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-indigo-600 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-5"
                                    >
                                        <SearchIcon
                                            size={17}
                                            aria-hidden="true"
                                        />
                                        <span className="hidden sm:inline">
                                            {status === "loading"
                                                ? "Searching..."
                                                : "Search"}
                                        </span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        aria-label="Clear search"
                                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 sm:w-auto sm:px-5"
                                    >
                                        <RotateCcw
                                            size={16}
                                            aria-hidden="true"
                                        />
                                        <span className="hidden sm:inline">
                                            Clear
                                        </span>
                                    </button>
                                </form>
                            </div>

                            {validationError && (
                                <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
                                    {validationError}
                                </div>
                            )}
                            {limitError && (
                                <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
                                    {limitError}
                                </div>
                            )}
                            {campaignPreparationError && (
                                <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
                                    {campaignPreparationError}
                                </div>
                            )}
                        </div>

                        <div
                            className={`order-4 flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm sm:rounded-3xl sm:py-8 lg:h-[var(--search-workspace-panel-height)] lg:min-h-0 lg:p-5 ${
                                shouldShowRecentSearches ? "" : "lg:col-span-2"
                            }`}
                        >
                            <div className="mb-4 flex shrink-0 flex-col gap-3 sm:mb-6 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-900">
                                        Lead Results
                                    </h3>
                                    <p className="mt-1 text-sm text-slate-500">
                                        Export current results as CSV when leads
                                        are available.
                                    </p>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                                    {canShowCampaignActions && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={handleViewCampaigns}
                                                className="inline-flex min-w-0 flex-1 items-center justify-center gap-1 rounded-xl border border-indigo-100 bg-indigo-50 px-2 py-2.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 sm:flex-none sm:gap-2 sm:rounded-xl sm:px-6 sm:py-3 sm:text-sm"
                                            >
                                                <ListChecks
                                                    size={16}
                                                    className="shrink-0"
                                                    aria-hidden="true"
                                                />
                                                <span className="sm:hidden">
                                                    Campaigns
                                                </span>
                                                <span className="hidden sm:inline">
                                                    View Campaigns
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCreateCampaign}
                                                disabled={isPreparingCampaign}
                                                className="inline-flex min-w-0 flex-1 items-center justify-center gap-1 rounded-xl bg-indigo-600 px-2 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:gap-2 sm:rounded-xl sm:px-6 sm:py-3 sm:text-sm"
                                            >
                                                <Rocket
                                                    size={16}
                                                    className="shrink-0"
                                                    aria-hidden="true"
                                                />
                                                <span className="sm:hidden">
                                                    Create
                                                </span>
                                                <span className="hidden sm:inline">
                                                    Create Campaign
                                                </span>
                                            </button>
                                        </>
                                    )}

                                    <button
                                        type="button"
                                        onClick={handleExportCsv}
                                        disabled={!canExportCurrentResults}
                                        title={exportCsvTitle}
                                        className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1 rounded-xl bg-emerald-500 px-2 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:gap-2 sm:rounded-xl sm:px-6 sm:py-3 sm:text-sm ${
                                            canShowCampaignActions
                                                ? ""
                                                : "basis-full sm:basis-auto"
                                        }`}
                                    >
                                        <Download
                                            size={16}
                                            className="shrink-0"
                                            aria-hidden="true"
                                        />
                                        <span className="sm:hidden">CSV</span>
                                        <span className="hidden sm:inline">
                                            Export CSV
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div className="min-h-0 lg:flex lg:flex-1 lg:flex-col">
                                {isPreparingCampaign && (
                                    <div className="mb-3 rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-indigo-800">
                                        <div className="flex items-center gap-2 font-semibold">
                                            <span>{`Preparing campaign for "${preparingCampaignQuery}"...`}</span>
                                            <span className="flex items-center gap-1">
                                                <span className="h-2 w-2 animate-bounce rounded-full bg-indigo-600" />
                                                <span
                                                    className="h-2 w-2 animate-bounce rounded-full bg-indigo-600"
                                                    style={{
                                                        animationDelay: "120ms",
                                                    }}
                                                />
                                                <span
                                                    className="h-2 w-2 animate-bounce rounded-full bg-indigo-600"
                                                    style={{
                                                        animationDelay: "240ms",
                                                    }}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {shouldShowLeadLoading &&
                                    !isPreparingCampaign && (
                                        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-indigo-800">
                                            <div className="flex items-center gap-2 font-semibold">
                                                <span>Searching</span>
                                                <span className="flex items-center gap-1">
                                                    <span className="h-2 w-2 animate-bounce rounded-full bg-indigo-600" />
                                                    <span
                                                        className="h-2 w-2 animate-bounce rounded-full bg-indigo-600"
                                                        style={{
                                                            animationDelay:
                                                                "120ms",
                                                        }}
                                                    />
                                                    <span
                                                        className="h-2 w-2 animate-bounce rounded-full bg-indigo-600"
                                                        style={{
                                                            animationDelay:
                                                                "240ms",
                                                        }}
                                                    />
                                                </span>
                                            </div>
                                            <p className="mt-1 text-sm">
                                                Fetching lead results...
                                            </p>
                                        </div>
                                    )}

                                {shouldShowProcessing &&
                                    !isPreparingCampaign && (
                                        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-indigo-800">
                                            <p className="font-semibold">
                                                Search is still processing
                                            </p>
                                            <p className="mt-1 text-sm">
                                                Results will appear here
                                                automatically when the search is
                                                complete.
                                            </p>
                                        </div>
                                    )}

                                {error && (
                                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                                        {error}
                                    </div>
                                )}

                                {isSearchNotFoundForCurrentSearch &&
                                    !error && (
                                        <div className="flex min-h-[260px] flex-1 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/70 p-5 text-center">
                                            <div className="max-w-sm">
                                                <SearchX
                                                    size={24}
                                                    className="mx-auto text-indigo-500"
                                                    aria-hidden="true"
                                                />
                                                <p className="mt-3 text-sm font-semibold text-slate-800">
                                                    No lead results found
                                                </p>
                                                <p className="mt-1 text-sm text-slate-500">
                                                    We couldn&apos;t find results
                                                    for this search. Please try
                                                    searching again.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                {shouldShowResults &&
                                    totalLeadCount === 0 &&
                                    !error && (
                                        <div className="flex min-h-[260px] flex-1 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/70 p-5 text-center">
                                            <div className="max-w-sm">
                                                <SearchIcon
                                                    size={24}
                                                    className="mx-auto text-indigo-500"
                                                    aria-hidden="true"
                                                />
                                                <p className="mt-3 text-sm font-semibold text-slate-800">
                                                    No lead results found
                                                </p>
                                                <p className="mt-1 text-sm text-slate-500">
                                                    We found 0 results for this
                                                    search.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                {!isPreparingCampaign &&
                                    !shouldShowLeadLoading &&
                                    !shouldShowProcessing &&
                                    !isSearchNotFoundForCurrentSearch &&
                                    !error &&
                                    !shouldShowResults && (
                                        <div className="flex min-h-[260px] flex-1 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/70 p-5 text-center">
                                            <div className="max-w-sm">
                                                <SearchIcon
                                                    size={24}
                                                    className="mx-auto text-indigo-500"
                                                    aria-hidden="true"
                                                />
                                                <p className="mt-3 text-sm font-semibold text-slate-800">
                                                    Search for leads or select
                                                    an existing search to view
                                                    results.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                {shouldShowResults && totalLeadCount > 0 && (
                                    <div className="product-scrollbar max-h-[420px] overflow-y-auto sm:max-h-[520px] lg:min-h-0 lg:flex-1 lg:max-h-none">
                                        <div className="border-t border-slate-100">
                                            {leadResults.map((lead, index) => {
                                                const leadRowId =
                                                    lead.id || `lead-${index}`;

                                                return (
                                                    <div
                                                        key={leadRowId}
                                                        className="border-b border-slate-100 px-3.5 py-3 text-left transition-colors odd:bg-slate-50 even:bg-white hover:bg-amber-50/50 sm:px-4 sm:py-4"
                                                    >
                                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                            <div className="min-w-0 flex-1">
                                                                <h3 className="break-words text-sm font-semibold text-slate-900 sm:text-base">
                                                                    {lead.name ||
                                                                        "Unnamed lead"}
                                                                </h3>

                                                                <p className="mt-1 text-sm text-slate-500">
                                                                    {lead.company ||
                                                                        "Company unavailable"}
                                                                    {lead.designation
                                                                        ? ` · ${lead.designation}`
                                                                        : ""}
                                                                </p>

                                                                {showDetail ===
                                                                    leadRowId && (
                                                                    <div
                                                                        className="mt-3 grid grid-cols-1 gap-3 rounded-xl border border-indigo-100 bg-indigo-50/50 p-3 sm:grid-cols-2"
                                                                        onCopy={
                                                                            preventDetailClipboard
                                                                        }
                                                                        onCut={
                                                                            preventDetailClipboard
                                                                        }
                                                                        onPaste={
                                                                            preventDetailClipboard
                                                                        }
                                                                    >
                                                                        <div>
                                                                            <p className="text-xs font-semibold uppercase text-slate-500">
                                                                                Email
                                                                            </p>
                                                                            <p className="mt-1 select-none break-words text-sm font-medium text-slate-800">
                                                                                {Array.isArray(
                                                                                    lead.emails,
                                                                                )
                                                                                    ? lead.emails.join(
                                                                                          ", ",
                                                                                      ) ||
                                                                                      "N/A"
                                                                                    : lead.email ||
                                                                                      "N/A"}
                                                                            </p>
                                                                        </div>

                                                                        <div>
                                                                            <p className="text-xs font-semibold uppercase text-slate-500">
                                                                                Phone
                                                                            </p>
                                                                            <p className="mt-1 select-none break-words text-sm font-medium text-slate-800">
                                                                                {Array.isArray(
                                                                                    lead.phones,
                                                                                )
                                                                                    ? lead.phones.join(
                                                                                          ", ",
                                                                                      ) ||
                                                                                      "N/A"
                                                                                    : lead.phone ||
                                                                                      "N/A"}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="shrink-0 sm:self-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleViewDetail(
                                                                            leadRowId,
                                                                        )
                                                                    }
                                                                    className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                                                                >
                                                                    {showDetail ===
                                                                    leadRowId ? (
                                                                        <EyeOff
                                                                            size={
                                                                                16
                                                                            }
                                                                            aria-hidden="true"
                                                                        />
                                                                    ) : (
                                                                        <Eye
                                                                            size={
                                                                                16
                                                                            }
                                                                            aria-hidden="true"
                                                                        />
                                                                    )}
                                                                    {showDetail ===
                                                                    leadRowId
                                                                        ? "Hide Details"
                                                                        : "View Details"}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {shouldShowResults && totalLeadCount > 0 && (
                                    <div className="mt-3 shrink-0">
                                        <p className="text-sm font-medium text-slate-600">
                                            Showing {totalLeadCount} of{" "}
                                            {totalLeadCount} leads
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            <SearchHistoryPanel
                isOpen={isHistoryPanelOpen}
                searches={searchHistory}
                status={searchHistoryStatus}
                error={searchHistoryError}
                activeSearchId={
                    hasActiveSearchWithVisibleResults ? activeSearchId : ""
                }
                isPreparingCampaign={isPreparingCampaign}
                onClose={() => setIsHistoryPanelOpen(false)}
                onSelectSearch={handleSelectHistorySearch}
                onViewCampaigns={handleViewCampaignsFromHistory}
                onCreateCampaign={handleCreateCampaignFromHistory}
            />
            <CampaignBlueprintModal
                isOpen={isCampaignModalOpen}
                onClose={handleCloseCampaignModal}
                activeSearchId={activeSearchId}
                organizationId={organizationContext?.organization_id}
                leadIds={campaignLeadIds}
                startAtDesignStep={shouldStartBlueprintAtDesignStep}
            />
            </PageContainer>
            <FloatingActionsMenu
                actions={pageActions}
                visible={!areTopActionsVisible}
            />
        </div>
    );
};

export default SearchLeads;
